/**
 * After Stripe redirects post-checkout. Polls /billing/session/{id}
 * until the subscription becomes active, then sends user to /dashboard.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MarketingHeader from "@/components/MarketingHeader";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refreshBilling } = useAuth();
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    if (polled.current) return;
    polled.current = true;
    let attempts = 0;
    const max = 12; // ~2 min @ 10s

    const tick = async () => {
      attempts++;
      try {
        const r = await api.get(`/billing/session/${sessionId}`);
        if (
          r.data.payment_status === "paid" ||
          ["active", "trialing"].includes(r.data.subscription_status)
        ) {
          if (refreshBilling) await refreshBilling();
          setState({ status: "ok" });
          setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
          return;
        }
        if (attempts >= max) {
          setState({ status: "timeout" });
          return;
        }
        setState({ status: "polling", attempts });
        setTimeout(tick, 10000);
      } catch (e) {
        if (attempts >= max) {
          setState({ status: "error" });
          return;
        }
        setTimeout(tick, 10000);
      }
    };
    tick();
  }, [sessionId, navigate, refreshBilling]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSubscribe={false} showSignIn={false} />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-neutral-400" strokeWidth={1.5} />
            <h1 className="font-display text-2xl font-semibold mt-6">
              Finalizing your subscription…
            </h1>
            <p className="text-neutral-500 text-sm mt-2">
              Stripe is confirming the charge. Usually 5–15 seconds.
            </p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" strokeWidth={1.5} />
            <h1 className="font-display text-3xl font-semibold mt-6">
              You're in.
            </h1>
            <p className="text-neutral-400 text-sm mt-3">
              Redirecting to your dashboard…
            </p>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" strokeWidth={1.5} />
            <h1 className="font-display text-2xl font-semibold mt-6">
              {state.status === "timeout" ? "Still confirming…" : "Something went wrong"}
            </h1>
            <p className="text-neutral-500 text-sm mt-2 max-w-sm mx-auto">
              If your card was charged, refresh in a minute - the subscription
              should activate via webhook. Otherwise contact support.
            </p>
            <a
              href="/billing"
              className="mt-6 inline-block text-white text-sm underline"
            >
              Back to billing
            </a>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
